import React, { useState } from "react";
import MortgagePieChart from "./MortgagePieChart";
import MortgageOverTimeChart from "./MortgageOverTimeChart";
import MortgageAmortizationTable from "./MortgageAmortizationTable";

interface MortgageResultProps {
    total: number;
    principalAndInterest: number;
    taxesAndInsurance: number;
    loanAmount: number;
    annualRate: number;
    loanTerm: number;
    toCurrency: (value: number) => string;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
}

const MortgageResult: React.FC<MortgageResultProps> = ({
    total,
    principalAndInterest,
    taxesAndInsurance,
    loanAmount,
    annualRate,
    loanTerm,
    toCurrency,
    isOpen,
    setIsOpen,
}) => {
    const [selectedTab, setSelectedTab] = useState<"breakdown" | "overtime" | "amortization">("breakdown");

    return (
        <div className="border border-gray-300 shadow 2xl:w-[900px]">
            {/* Clickable Header */}
            <button
                type="button"
                className="w-full bg-[#1275ba] text-white px-4 py-3 cursor-pointer select-none flex items-center justify-between"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="flex flex-col w-[274px] sm:w-full 2xl:flex 2xl:flex-row 2xl:items-center 2xl:justify-center text-center 2xl:w-[900px]">
                    <span className="text-[14px] 2xl:text-[18px] font-semibold uppercase ">
                        Your Total Monthly Mortgage Payment Will Be
                    </span>
                    <span className="text-[18px] font-bold 2xl:pl-1">{toCurrency(total)}</span>
                </div>
                <span className="text-xl">{isOpen ? "−" : "+"}</span>
            </button>

            {/* Expandable Content */}
            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? "opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                {/* Breakdown Row */}
                <div className="grid grid-cols-2 text-sm text-black font-semibold border text-center border-r border-gray-300  2xl:w-[874px] mx-auto 2xl:mt-4">
                    <div className="py-2 border-r border-gray-300">
                        <strong className="text-[15px]">Principal and Interest:</strong>{" "}
                        <span className="text-[#1275ba] font-bold text-[22px] ">
                            {toCurrency(principalAndInterest)}
                        </span>
                    </div>
                    <div className="py-2 border-r border-gray-300">
                        <strong className="text-[15px]">Property Taxes and Insurance:</strong>{" "}
                        <span className="text-[#1275ba] font-bold text-[22px]">{toCurrency(taxesAndInsurance)}</span>
                    </div>
                </div>

                <div className="pt-8 flex flex-col w-[274px] sm:w-full mx-auto 2xl:flex 2xl:flex-row 2xl:items-center 2xl:justify-center text-center  2xl:pt-8 2xl:w-[900px]">
                    <span className="text-[24px] font-semibold uppercase">
                        Your Total Monthly Mortgage Payment Will Be
                    </span>
                    <span className="text-[24px] font-bold 2xl:pl-1">{toCurrency(total)}</span>
                </div>

                {/* Tabs */}
                <div className="flex flex-col gap-1 items-center 2xl:justify-center 2xl:flex-row 2xl:mt-4 2xl:space-x-2 px-4">
                    {["breakdown", "overtime", "amortization"].map((tab) => (
                        <button
                            key={tab}
                            className={`w-[180px] sm:w-full px-4 py-2 font-bold uppercase rounded 2xl:w-[180px] ${
                                selectedTab === tab
                                    ? "bg-blue text-white"
                                    : "bg-darkBlue text-white hover:bg-blue hover:text-white focus:bg-blue"
                            }`}
                            onClick={() => setSelectedTab(tab as typeof selectedTab)}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="mt-6 px-4 pb-2">
                    {selectedTab === "breakdown" && (
                        <MortgagePieChart
                            principalAndInterest={principalAndInterest}
                            taxesAndInsurance={taxesAndInsurance}
                        />
                    )}
                    {selectedTab === "overtime" && (
                        <MortgageOverTimeChart principal={loanAmount} annualRate={annualRate} years={loanTerm} />
                    )}
                    {selectedTab === "amortization" && (
                        <MortgageAmortizationTable
                            loanAmount={loanAmount}
                            annualRate={annualRate}
                            loanTerm={loanTerm}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default MortgageResult;
